import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useCallkitDataKeys } from '../model';
import { getLocalUserName, isOwnUserReconnecting } from '../utils/helper';
import { setCallUIState, setViewMode, setShowParticipantsPanel } from '../model/callkitDataSlice';
import { CALLKIT_CALLUISTATE, CALLKIT_VIEWMODE, CALL_MODES } from '../constants';
import { fetchUsersInfo } from '@/features/users';
import { hidePopup, showPopup } from '@/components/PopupContainer/popupService';
import { GroupCallSelectionPopup } from '@/shared/components/GroupCallSelectionPopup';
import { showInfoToast, showWarningToast } from '@/shared/utils';
import config from '@/config';
import { useLanguage } from '@/app/providers';

/**
 * ViewModel for TopBar.
 * Owns: invite popup state, toggle, remoteUser derivation, minimizing call.
 * Cross-feature fix: TopBar no longer imports from features/chats;
 * it imports GroupCallSelectionPopup from @/shared/components.
 */
export const useTopBar = () => {
   const { t } = useLanguage();
   const dispatch = useDispatch();
   const {
      ongoingCallParticipants = [],
      groupId,
      callType,
      viewMode,
      callMode,
   } = useCallkitDataKeys(['ongoingCallParticipants', 'groupId', 'callType', 'viewMode', 'callMode']);

   const localUser = getLocalUserName();
   const remoteUser = ongoingCallParticipants.find(p => p.userId !== localUser);

   const toggleInvitePopup = useCallback(() => {
      if (isOwnUserReconnecting()) {
         showWarningToast(t('common.INTERNET_ERROR_MESSAGE'));
         return;
      }
      const isMeet = callMode === CALL_MODES.MEET;

      // Max-participant guard is irrelevant for MEET — anyone joins via link, not by invite
      if (!isMeet) {
         const currentParticipantCount = ongoingCallParticipants.filter(p => !p.userId.endsWith('-screen')).length;
         if (currentParticipantCount >= config.maxCallParticipants) {
            showInfoToast(t('calls.MAX_LIMIT_REACHED', { count: config.maxCallParticipants }));
            return;
         }
      }

      dispatch(setShowParticipantsPanel(false));
      showPopup({
         id: 'groupCallSelectionModal',
         visible: true,
         className: 'groupCallSelectionPopup',
         title: isMeet ? t('common.CALL_LINK') : t('group.ADD_PARTICIPANTS'),
         body: (
            <GroupCallSelectionPopup
               activeChatJid={groupId || ''}
               callType={callType}
               onClose={() => hidePopup('groupCallSelectionModal')}
               mode="invite"
               existingUsers={ongoingCallParticipants.map(p => p.userId)}
               linkOnly={isMeet}
            />
         ),
         onRequestClose: () => hidePopup('groupCallSelectionModal'),
      });
   }, [groupId, callType, callMode, ongoingCallParticipants, t, dispatch]);

   const minimizeCall = useCallback(() => {
      dispatch(setCallUIState(CALLKIT_CALLUISTATE.MINIMIZED));
   }, [dispatch]);

   const toggleViewMode = useCallback(() => {
      dispatch(setViewMode(viewMode === CALLKIT_VIEWMODE.TILED ? CALLKIT_VIEWMODE.SIDEBAR : CALLKIT_VIEWMODE.TILED));
   }, [dispatch, viewMode]);

   const usersInfo = useSelector(state => state.userInfoData?.users || {});

   // ── Fetch missing group info ──────────────────────────────────────────────
   useEffect(() => {
      if (groupId && !usersInfo[groupId] && callMode === CALL_MODES.ONETOMANY) {
         fetchUsersInfo([groupId]);
      }
   }, [groupId, usersInfo, callMode]);

   const callTitle = useMemo(() => {
      if (callMode === CALL_MODES.ONETOMANY && groupId && usersInfo[groupId]?.nickName) {
         return usersInfo[groupId].nickName;
      }
      if (callMode === CALL_MODES.MEET) return 'MirrorFly Meet';

      const participants = ongoingCallParticipants.filter(p => p.userId !== localUser && !p.userId.endsWith('-screen'));
      const names = participants.map(p => usersInfo[p.userId]?.nickName || p.userId);
      if (names.length === 0) return 'MirrorFly Call';
      if (names.length === 1) return names[0];
      if (names.length === 2) return `${names[0]}, ${names[1]}`;
      return `${names[0]}, ${names[1]} & +${names.length - 1} others`;
   }, [ongoingCallParticipants, usersInfo, localUser, callMode, groupId]);

   const participantCount = useMemo(() => {
      return (ongoingCallParticipants || []).filter(p => !p.userId?.endsWith('-screen')).length;
   }, [ongoingCallParticipants]);

   return {
      groupId,
      callType,
      remoteUser,
      localUser,
      ongoingCallParticipants,
      participantCount,
      toggleInvitePopup,
      minimizeCall,
      viewMode,
      callMode,
      toggleViewMode,
      callTitle,
   };
};
